import {
  CellValues,
  ColumnHeading,
  EntityFilterControlsConfig,
  PaginatedDataTable,
  TableCellStylesFn,
  Text,
  useVisibleHeadings,
} from '@lbcamden/lib-react';
import { FunctionComponent, useCallback } from 'react';
import { useNavigate } from 'react-router';

import {
  useLeadOfficerOpenedCaseList,
  useCurrentUser,
  useGlobalStore,
  useRoleInformation,
} from '~/client/hooks/storeHooks';
import { api } from '~/client/lib/trpc';
import { toPropertyPage } from '~/client/utils/navigation';
import { Case } from '~/common/types/cases';

import { PropertySelectPageList } from './PropertySelectPage.styled';

type ColumnId = StringKey<Case> | 'address';

const headings = [
  { id: 'caseId', content: 'Case number' },
  { id: 'demand', content: 'Type' },
  { id: 'address', content: 'Address' },
] as const satisfies ColumnHeading<ColumnId>[];

const cellStyles: TableCellStylesFn<ColumnId> = (columnId) => (theme) => ({
  width: columnId === 'caseId' ? 35 : 100,
  maxWidth: columnId === 'caseId' ? 35 : 100,
  [theme.breakpoints.down('md')]: {
    width: 'auto',
  },
});

const filterConfigs: EntityFilterControlsConfig<Case> = [
  [
    {
      type: 'toggle',
      key: 'isVisitingProgramme',
      label: 'Visiting Programme',
      activeValue: true,
    },
    {
      type: 'toggle',
      key: 'isVisitingProgramme',
      label: 'Other',
      activeValue: false,
    },
  ],
];

const mapCaseToRow = (item: Case, address: string | undefined): CellValues<ColumnId> => ({
  ...item,
  address,
});

export const PropertySelectPageListing: FunctionComponent = () => {
  const user = useCurrentUser();
  const { isNeighbourhood } = useRoleInformation();

  const globalStore = useGlobalStore();

  api.syncCasesByLeadOfficer.useQuery(undefined, { enabled: isNeighbourhood });

  const getCasePropertyAddress = ({ propertyId }: Case) =>
    globalStore!.get('properties', propertyId)?.address;

  const cases = useLeadOfficerOpenedCaseList(user!.camdenAccountName).filter(
    getCasePropertyAddress,
  );

  const memoMapItemToRow = useCallback(
    (item: Case) => mapCaseToRow(item, getCasePropertyAddress(item)),
    [globalStore],
  );

  const hasVisitingProgrammeCase = cases.some((item) => item.isVisitingProgramme);

  const navigate = useNavigate();

  const visibleHeadings = useVisibleHeadings(headings, {
    xs: headings.slice(1, 3),
    sm: headings.slice(1, 3),
  });

  const onClickRow = useCallback(
    ({ propertyId }: Case) => navigate(toPropertyPage(propertyId)),
    [],
  );

  if (!cases.length) {
    return null;
  }

  return (
    <PropertySelectPageList data-testid="Property Select - officer case list">
      <Text variant="h1">or pick one from your open case list</Text>
      <PaginatedDataTable
        css={{ padding: 0 }}
        cellStyles={cellStyles}
        headings={visibleHeadings}
        filterConfigs={filterConfigs}
        initialFilterValues={{ isVisitingProgramme: hasVisitingProgrammeCase || undefined }}
        items={cases}
        mapItemToRow={memoMapItemToRow}
        onClickRow={onClickRow}
      />
    </PropertySelectPageList>
  );
};
